/*
 * Persistence — window.storage in-artifact, localStorage everywhere else.
 *
 * Schema is identical to v0.2; no migration needed. The key (`fernhollow-save`)
 * matches v0.2 exactly so a player's existing saved critter survives the refactor.
 *
 * Sanitization: indices into FUR_COLORS and HATS are clamped so a future content
 * change can't crash the renderer with an out-of-range index.
 */

import type { SaveData } from '@/state/types';
import { FUR_COLORS } from '@/content/furColors';
import { HATS, DEFAULT_UNLOCKS } from '@/content/hats';
import { NECKWEAR, DEFAULT_NECK_UNLOCKS } from '@/content/neckwear';
import { DEFAULT_NAMES } from '@/content/phrases';

/**
 * Window (in hours) that counts as "consecutive." If the gap between this boot
 * and the previous session's timestamp is within this window, we increment the
 * streak; otherwise we reset to 1. 36h is generous enough that a player who
 * comes back the next evening doesn't lose their streak to a late bedtime.
 */
const CONSECUTIVE_SESSION_WINDOW_HOURS = 36;

const STORAGE_KEY = 'fernhollow-save';

/** Mutable runtime save object. The rest of the app reads/writes this. */
export const save: SaveData = createDefaultSave();

function createDefaultSave(): SaveData {
  const fallback = DEFAULT_NAMES[0] ?? 'Sprout';
  const pick = DEFAULT_NAMES[Math.floor(Math.random() * DEFAULT_NAMES.length)] ?? fallback;
  return {
    name: pick,
    color: 0,
    // Default to the mushroom hat (index 2) — matches v0.2 first-boot look.
    hat: 2,
    neck: 0,
    unlocks: [...DEFAULT_UNLOCKS],
    neckUnlocks: [...DEFAULT_NECK_UNLOCKS],
    bestForage: 0,
    bestFish: 0,
    lifetimeForage: 0,
    lifetimeFish: 0,
    chatCount: 0,
    playSeconds: 0,
    consecutiveSessions: 0,
    lastSessionAt: 0,
    firstRun: true,
  };
}

/**
 * Minimal shape we need from window.storage. The in-artifact API exposes
 * get/set that return string-valued envelopes; localStorage is sync, so we wrap
 * it in promises for a uniform interface.
 */
interface StorageEnvelope {
  value?: string | null;
}

interface AsyncStorage {
  get(key: string): Promise<StorageEnvelope | null>;
  set(key: string, value: string): Promise<unknown>;
}

declare global {
  interface Window {
    storage?: AsyncStorage;
  }
}

function backend(): AsyncStorage {
  if (typeof window !== 'undefined' && window.storage) {
    return window.storage;
  }
  return {
    async get(key: string) {
      try {
        const v = window.localStorage.getItem(key);
        return v === null ? null : { value: v };
      } catch {
        return null;
      }
    },
    async set(key: string, value: string) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        /* storage unavailable (private browsing / quota) — silently drop */
      }
    },
  };
}

function sanitize(data: Partial<SaveData>): void {
  if (typeof data.name === 'string' && data.name) save.name = data.name;
  if (
    typeof data.color === 'number' &&
    data.color >= 0 &&
    data.color < FUR_COLORS.length
  ) save.color = data.color;
  if (
    typeof data.hat === 'number' &&
    data.hat >= 0 &&
    data.hat < HATS.length
  ) save.hat = data.hat;
  if (
    typeof data.neck === 'number' &&
    data.neck >= 0 &&
    data.neck < NECKWEAR.length
  ) save.neck = data.neck;
  if (Array.isArray(data.unlocks)) save.unlocks = data.unlocks.filter(
    (n): n is string => typeof n === 'string'
  );
  if (Array.isArray(data.neckUnlocks)) save.neckUnlocks = data.neckUnlocks.filter(
    (n): n is string => typeof n === 'string'
  );
  if (typeof data.bestForage === 'number') save.bestForage = data.bestForage;
  if (typeof data.bestFish === 'number') save.bestFish = data.bestFish;
  if (typeof data.lifetimeForage === 'number') save.lifetimeForage = data.lifetimeForage;
  if (typeof data.lifetimeFish === 'number') save.lifetimeFish = data.lifetimeFish;
  if (typeof data.chatCount === 'number') save.chatCount = data.chatCount;
  if (typeof data.playSeconds === 'number') save.playSeconds = data.playSeconds;
  if (typeof data.consecutiveSessions === 'number') save.consecutiveSessions = data.consecutiveSessions;
  if (typeof data.lastSessionAt === 'number') save.lastSessionAt = data.lastSessionAt;
}

/**
 * Load persisted save into `save`. Returns true if existing data was found.
 * On first run, returns false and leaves the defaults in place.
 */
export async function loadSave(): Promise<boolean> {
  try {
    const r = await backend().get(STORAGE_KEY);
    if (r && r.value) {
      const d = JSON.parse(r.value) as Partial<SaveData>;
      sanitize(d);
      save.firstRun = false;
      return true;
    }
  } catch {
    /* corrupt entry — fall back to defaults */
  }
  return false;
}

/** Persist current `save` to whichever backend is available. Fire-and-forget. */
export async function persist(): Promise<void> {
  try {
    await backend().set(STORAGE_KEY, JSON.stringify(save));
  } catch {
    /* swallow — UI shouldn't block on save failures */
  }
}

/**
 * Call once per boot, after loadSave(), to bump the consecutive-sessions
 * counter. Returns the new streak count (>=1).
 *
 * Rules:
 *  - First boot ever: streak = 1.
 *  - Within {@link CONSECUTIVE_SESSION_WINDOW_HOURS} of last boot: increment.
 *  - Beyond that window: reset to 1.
 */
export function bumpSessionStreak(): number {
  const now = Date.now();
  const last = save.lastSessionAt;
  if (last <= 0) {
    save.consecutiveSessions = 1;
  } else {
    const hours = (now - last) / 1000 / 3600;
    if (hours <= CONSECUTIVE_SESSION_WINDOW_HOURS) {
      // Same day or back within the window — preserve / extend the streak.
      // Don't double-count a single tab refresh: if the previous session was
      // within the last hour, leave the count untouched.
      if (hours >= 1) save.consecutiveSessions += 1;
      if (save.consecutiveSessions < 1) save.consecutiveSessions = 1;
    } else {
      save.consecutiveSessions = 1;
    }
  }
  save.lastSessionAt = now;
  void persist();
  return save.consecutiveSessions;
}
